import { memo, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import { View } from 'react-native'
import { DropdownProps } from 'react-native-element-dropdown/lib/typescript/components/Dropdown/model'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import { getErrorField } from '@/components/form/formUtils'
import Skeleton from '@/components/shared/Skeleton'
import UniDropdown from '@/components/unistyles/UniDropdown'
import UniHelperText from '@/components/unistyles/UniHelperText'
import UniText from '@/components/unistyles/UniText'

type data = { label: string; value: string }

interface Props<C extends FieldValues, E extends FieldErrors> {
  control: Control<C, any>
  data: data[]
  errors?: E
  fieldName: Path<C>
  isLoading?: boolean
  label: string
  required: boolean
}

const FormFieldSelect = <C extends FieldValues, E extends FieldErrors>({
  control,
  data,
  errors,
  fieldName,
  label,
  required,
  style,
  isLoading,
  onChange: onChangeFromProps,
  ...props
}: Props<C, E> & Partial<DropdownProps<data>>) => {
  const { theme } = useUnistyles()

  const [selectedUniDropdownValue, setSelectedUniDropdownValue] =
    useState<data | null>(null)

  return (
    <View style={[style, styles.root]}>
      <Skeleton isLoading={isLoading}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { onChange, onBlur, value } }) => {
            const selectedItem = value
              ? (data.find((d) => d.value === value) ?? {
                  label: String(value),
                  value: String(value),
                })
              : ''

            // If the current value isn't present in the provided `data`, include
            // a lightweight fallback item in the dataset so the UniDropdown can
            // display the selected label. We keep the original `data` intact
            // by prepending the fallback only when necessary.
            const dataWithFallback =
              selectedItem && !data.find((d) => d.value === selectedItem.value)
                ? [selectedItem, ...data]
                : data

            return (
              <View style={[style, styles.innerContainer]}>
                {(selectedUniDropdownValue || value) && (
                  <UniText
                    maxFontSizeMultiplier={1.5}
                    style={[
                      styles.label,
                      props.disable && {
                        color: theme.colors.onSurfaceDisabled,
                      },
                    ]}
                  >
                    {label}
                  </UniText>
                )}

                <UniDropdown
                  data={dataWithFallback}
                  dropdownPosition="auto"
                  getThemeProps={(t) => ({
                    activeColor: t.colors.primaryContainer,
                    containerStyle: {
                      backgroundColor: t.colors.background,
                      borderColor: t.colors.outline,
                      maxHeight: '80%' as const,
                    },
                    iconColor: getErrorField(fieldName, errors)
                      ? t.colors.error
                      : t.colors.onBackground,
                    itemContainerStyle: {
                      backgroundColor: t.colors.background,
                    },
                    itemTextStyle: { color: t.colors.onBackground },
                    placeholderStyle: [
                      { color: t.colors.onSurface },
                      props.disable && {
                        color: t.colors.onSurfaceDisabled,
                      },
                      getErrorField(fieldName, errors) && {
                        color: t.colors.error,
                      },
                    ],
                    selectedTextStyle: [
                      {
                        backgroundColor: t.colors.surface,
                        color: t.colors.onBackground,
                      },
                      props.disable && {
                        color: t.colors.onSurfaceDisabled,
                      },
                    ],
                  })}
                  inverted={false}
                  itemTestIDField={`${fieldName}-dropdown-item`}
                  labelField="label"
                  onBlur={onBlur}
                  onChange={(selected) => {
                    setSelectedUniDropdownValue(selected)
                    onChange(selected.value)
                    // Call parent onChange handler if provided
                    if (onChangeFromProps) {
                      onChangeFromProps(selected)
                    }
                  }}
                  placeholder={label}
                  selectedTextProps={{ testID: `${fieldName}-dropdown-text` }}
                  style={[
                    styles.dropdown,
                    getErrorField(fieldName, errors) && styles.errorOutline,
                  ]}
                  testID={`${fieldName}-dropdown`}
                  // pass either the found data item or a fallback item so the
                  // selected text is displayed even if the option isn't in `data`
                  value={selectedItem}
                  valueField="value"
                  {...props}
                />
                {getErrorField(fieldName, errors) && (
                  <UniHelperText
                    type="error"
                    visible={!!getErrorField(fieldName, errors)}
                  >
                    {getErrorField(fieldName, errors)?.message as string}
                  </UniHelperText>
                )}
              </View>
            )
          }}
          rules={{
            required: required,
          }}
        />
      </Skeleton>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    dropdown: {
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.outline,
      borderRadius: 4,
      borderWidth: 1,
      height: 50,
      paddingHorizontal: 12,
    },
    errorOutline: {
      borderColor: theme.colors.error,
      borderWidth: 2,
    },
    innerContainer: {
      marginTop: 6,
      position: 'relative',
      zIndex: 100,
    },
    // The values in this style are intentionally hardcoded to match the design of react-native-paper
    // this allows the component to be used as a near-visual-drop-in replacement for react-native-paper's text input
    label: {
      backgroundColor: theme.colors.surface,
      color: theme.colors.onSurfaceVariant,
      fontSize: 12,
      left: 5,
      paddingHorizontal: theme.tokens.spacing[2],
      position: 'absolute',
      top: -8,
      zIndex: 999,
    },
    root: {
      marginBottom: theme.tokens.spacing[2],
    },
  }
})

export default memo(FormFieldSelect) as typeof FormFieldSelect
